<template>
  <div class="flex flex-col w-full gap-4">
    <!-- Pro Series List Header -->
    <div class="rounded-t bg-gray-950 flex items-center justify-between">
      <h3 class="text-text-primary text-xl font-medium">
        {{ $t('coach.pro_series.title') }}
      </h3>

      <!-- Search Filters -->
      <div class="flex items-center gap-3">
        <!-- Team Filter -->
        <MultiSelectDropdown
          :items="teams"
          :logos="teamLogos"
          v-model:selectedItems="selectedTeams"
          :label="$t('coach.shared.teams')"
        />

        <!-- Event Filter -->
        <MultiSelectDropdown
          :items="events"
          v-model:selectedItems="selectedEvents"
          :label="$t('coach.pro_series.events')"
        />
      </div>
    </div>

    <!-- Loading State - Skeleton Loaders -->
    <ProSeriesSkeleton v-if="loading" :count="10" />

    <!-- Content Views (only show when not loading and has data) -->
    <div v-if="!loading && series.length > 0" class="relative">
      <!-- Filter Loading Overlay -->
      <div
        v-if="searchLoading"
        class="absolute inset-0 bg-gray-900/50 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg"
      >
        <div class="flex items-center gap-2 text-text-primary">
          <svg
            class="animate-spin h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span class="text-sm">{{ $t('coach.pro_series.filtering') }}</span>
        </div>
      </div>

      <!-- Mobile View -->
      <div class="flex flex-col w-full lg:hidden">
        <MobileSeriesRow
          v-for="seriesItem in series"
          :key="seriesItem.id"
          :series="seriesItem"
        />
      </div>

      <!-- Desktop View -->
      <div class="bg-gray-900 px-5 rounded-lg sm:px-6 hidden lg:block">
        <div class="sm:-mx-6">
          <div class="align-middle inline-block min-w-full">
            <table class="min-w-full divide-y divide-gray-950 table-auto">
              <tbody class="divide-y divide-gray-950">
                <SeriesRow
                  v-for="seriesItem in series"
                  :key="seriesItem.id"
                  :series="seriesItem"
                />
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="!loading && series.length === 0" class="flex flex-col items-center justify-center py-12">
      <div class="text-center">
        <h3 class="text-lg font-medium text-text-primary mb-2">{{ $t('coach.pro_series.none_found_title') }}</h3>
        <p class="text-text-secondary">{{ $t('coach.pro_series.none_found_body') }}</p>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="series.length > 0" class="flex items-center justify-between gap-4 mt-4">
      <div class="flex items-center gap-2">
        <p class="text-text-secondary text-xs">{{ $t('coach.pro_series.showing_range', { from: (page - 1) * 10 + 1, to: Math.min(page * 10, totalSeries), total: totalSeries }) }}</p>
      </div>

      <div v-if="totalPages > 1" class="flex items-center justify-end gap-2">
        <button
          class="p-2 text-text-secondary hover:text-text-primary disabled:opacity-50 disabled:hover:text-text-secondary"
          :disabled="page === 1"
          @click="changePage(page - 1)"
        >
          <div class="flex bg-gray-700 p-2 rounded-lg">
            <ArrowLeftIcon class="h-4 w-4 text-text-secondary" />
          </div>
        </button>

        <button
          v-if="showFirstPage"
          @click="changePage(1)"
          class="px-3 py-1 rounded-md text-sm text-text-secondary hover:text-text-primary"
          :class="{ 'text-primary-500': page === 1 }"
        >
          1
        </button>

        <span v-if="showFirstPage" class="text-text-secondary">...</span>

        <button
          v-for="pageNum in displayedPages"
          :key="pageNum"
          @click="changePage(pageNum)"
          class="px-3 py-1 rounded-md text-sm"
          :class="[
            page === pageNum
              ? 'text-primary-500'
              : 'text-text-secondary hover:text-text-primary',
          ]"
        >
          {{ pageNum }}
        </button>

        <span v-if="hasEllipsis" class="text-text-secondary">...</span>

        <button
          v-if="showLastPage && !displayedPages.includes(totalPages)"
          @click="changePage(totalPages)"
          class="px-3 py-1 rounded-md text-sm text-text-secondary hover:text-text-primary"
          :class="{ 'bg-primary-600 text-text-primary': page === totalPages }"
        >
          {{ totalPages }}
        </button>

        <button
          class="p-2 text-text-secondary hover:text-text-primary disabled:opacity-50 disabled:hover:text-text-secondary"
          :disabled="page === totalPages"
          @click="changePage(page + 1)"
        >
          <div class="flex bg-gray-700 p-2 rounded-lg">
            <ArrowRightIcon class="h-4 w-4 text-text-secondary" />
          </div>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref, computed, onMounted, watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/vue/20/solid';
import SeriesRow from './SeriesRow.vue';
import MobileSeriesRow from './MobileSeriesRow.vue';
import ProSeriesSkeleton from './ProSeriesSkeleton.vue';
import MultiSelectDropdown from '../../../General/MultiSelectDropdown.vue';
import { getBestOfFormat } from '../../../../utils/array_utils';

const route = useRoute();
const router = useRouter();

// State
const series = ref([]);
const totalSeries = ref(0);
const totalPages = ref(0);
const page = ref(1);
const loading = ref(false);
const searchLoading = ref(false);

// Filter data
const teams = ref([]);
const teamLogos = ref({});
const events = ref([]);
const queryList = (value) => [value].flat().filter(Boolean);
const selectedTeams = ref(queryList(route.query.teams));
const selectedEvents = ref(queryList(route.query.events));
const knownNames = (values, names) => (names.length ? values.map((v) => names.find((n) => n.toLowerCase() === v.toLowerCase())).filter(Boolean) : values);

// Computed

// Pagination computed properties (copied from RoutinesPage)
const displayedPages = computed(() => {
  const maxVisiblePages = 3;
  if (totalPages.value <= maxVisiblePages) {
    return Array.from({ length: totalPages.value }, (_, i) => i + 1);
  }

  let start = Math.max(1, page.value - Math.floor(maxVisiblePages / 2));
  const end = Math.min(start + maxVisiblePages - 1, totalPages.value);

  if (end - start + 1 < maxVisiblePages) {
    start = Math.max(1, end - maxVisiblePages + 1);
  }

  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
});

const hasEllipsis = computed(() => {
  const maxVisiblePages = 3;
  return totalPages.value > maxVisiblePages
         && !displayedPages.value.includes(totalPages.value);
});

const showLastPage = computed(() => {
  const maxVisiblePages = 3;
  return totalPages.value > maxVisiblePages;
});

const showFirstPage = computed(() => {
  const maxVisiblePages = 3;
  return totalPages.value > maxVisiblePages
         && !displayedPages.value.includes(1)
         && page.value > maxVisiblePages;
});

// Methods
// No client-side sorting; backend returns sorted data

function loadSeries(showLoading = true) {
  if (showLoading) {
    loading.value = true;
  } else {
    searchLoading.value = true;
  }

  const params = {
    page: page.value,
  };

  // Add filter parameters if they have values
  if (selectedTeams.value.length > 0) {
    params.teams = selectedTeams.value.join(',');
  }
  if (selectedEvents.value.length > 0) {
    params.events = selectedEvents.value.join(',');
  }

  axios.get('/match_series', {
    params,
  }).then((res) => {
    // Handle the new backend response structure
    let seriesData = [];
    if (res.data.match_series) {
      seriesData = res.data.match_series;
      totalPages.value = res.data.pagination?.total_pages || 1;
      totalSeries.value = res.data.pagination?.total_count || seriesData.length;
    } else if (Array.isArray(res.data)) {
      seriesData = res.data;
      totalPages.value = 1;
      totalSeries.value = seriesData.length;
    } else if (res.data?.series) {
      seriesData = res.data.series;
      totalPages.value = res.data.total_pages || 1;
      totalSeries.value = res.data.total_series || res.data.series_count || seriesData.length;
    } else if (res.data && typeof res.data === 'object') {
      // Fallback: Convert object with numeric keys to array
      seriesData = Object.values(res.data);
      totalPages.value = res.data.total_pages || 1;
      totalSeries.value = res.data.total_series || res.data.series_count || seriesData.length;
    }

    series.value = seriesData;
    loading.value = false;
    searchLoading.value = false;
  }).catch((error) => {
    loading.value = false;
    searchLoading.value = false;
    // Show empty state instead of mock data
    series.value = [];
    totalPages.value = 0;
    totalSeries.value = 0;
  });
}

function syncFiltersToUrl() {
  router.replace({
    query: {
      ...route.query,
      teams: selectedTeams.value.length ? selectedTeams.value : undefined,
      events: selectedEvents.value.length ? selectedEvents.value : undefined,
    },
  });
}

function changePage(newPage) {
  page.value = newPage;
  loadSeries();
}

// Load teams and events data
async function loadTeams() {
  try {
    const response = await axios.get('/match_series/teams', { params: { with_logos: 1 } });
    teams.value = (response.data || []).map((team) => team.name ?? team);
    teamLogos.value = Object.fromEntries((response.data || []).filter((team) => team.logo_url).map((team) => [team.name, team.logo_url]));
  } catch (error) {
    console.error('Error loading teams:', error);
    teams.value = [];
  }
}

async function loadEvents() {
  try {
    const response = await axios.get('/match_series/events');
    events.value = response.data || [];
  } catch (error) {
    console.error('Error loading events:', error);
    events.value = [];
  }
}

// Watchers for selected filters
watch(
  () => selectedTeams.value,
  () => {
    syncFiltersToUrl();
    if (loading.value) return;
    page.value = 1;
    loadSeries(false);
  },
  { deep: true },
);

watch(
  () => selectedEvents.value,
  () => {
    syncFiltersToUrl();
    if (loading.value) return;
    page.value = 1;
    loadSeries(false);
  },
  { deep: true },
);

// Lifecycle
onMounted(async () => {
  const lists = Promise.all([loadTeams(), loadEvents()]);
  if (selectedTeams.value.length || selectedEvents.value.length) {
    await lists;
    selectedTeams.value = knownNames(selectedTeams.value, teams.value);
    selectedEvents.value = knownNames(selectedEvents.value, events.value);
  }
  await Promise.all([lists, loadSeries()]);
});
</script>

<style scoped>
.grow-transition {
  transition: width 0.3s ease;
}
</style>
